import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, BarChart3, FileText, LogOut, ChevronDown, User, ClipboardList, Settings, Briefcase, Globe, Brain, BookOpen, Contact, Tag, MessageCircle, ScrollText, GitBranch, TrendingUp, LifeBuoy, Bell, LayoutTemplate, Sparkles, Activity, Star } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_WIDTH = '16rem';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [vacanciesOpen, setVacanciesOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const [adminUser, setAdminUser] = useState(() => {
    try {
      const user = localStorage.getItem('admin_user');
      return user ? JSON.parse(user) : {};
    } catch (e) {
      return {};
    }
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const linkClass = (path: string) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive(path) ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
    }`;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className="fixed left-0 top-0 bottom-0 bg-white border-r border-gray-200 flex flex-col z-30"
        style={{ width: SIDEBAR_WIDTH }}
      >
        <Link to="/" className="flex items-center gap-2 p-4 border-b border-gray-200">
          <FileText className="h-8 w-8 text-blue-600 flex-shrink-0" />
          <span className="text-lg font-bold text-gray-900 truncate">РаботаМакс Admin</span>
        </Link>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          <Link to="/" className={linkClass('/')}>
            <BarChart3 className="h-5 w-5 flex-shrink-0" />
            Дашборд
          </Link>
          <Link to="/users" className={linkClass('/users')}>
            <Users className="h-5 w-5 flex-shrink-0" />
            Пользователи
          </Link>
          <Link to="/resumes" className={linkClass('/resumes')}>
            <Contact className="h-5 w-5 flex-shrink-0" />
            Резюме
          </Link>
          <Link to="/consultant" className={linkClass('/consultant')}>
            <MessageCircle className="h-5 w-5 flex-shrink-0" />
            Наставник
          </Link>
          <Link to="/surveys" className={linkClass('/surveys')}>
            <ClipboardList className="h-5 w-5 flex-shrink-0" />
            Опросы
          </Link>
          <Link to="/support" className={linkClass('/support')}>
            <LifeBuoy className="h-5 w-5 flex-shrink-0" />
            Поддержка
          </Link>
          <Link to="/company-reviews" className={linkClass('/company-reviews')}>
            <Star className="h-5 w-5 flex-shrink-0" />
            Отзывы о компаниях
          </Link>
          <Link to="/notifications" className={linkClass('/notifications')}>
            <Bell className="h-5 w-5 flex-shrink-0" />
            Уведомления
          </Link>
          <Link to="/notifications/templates" className={linkClass('/notifications/templates')}>
            <LayoutTemplate className="h-5 w-5 flex-shrink-0" />
            Шаблоны уведомлений
          </Link>
          <Link to="/salary-analysis" className={linkClass('/salary-analysis')}>
            <TrendingUp className="h-5 w-5 flex-shrink-0" />
            Анализ зарплаты
          </Link>
          <Link to="/salary-market" className={linkClass('/salary-market')}>
            <TrendingUp className="h-5 w-5 flex-shrink-0" />
            Зарплатный рынок
          </Link>
          <Link to="/hypotheses-funnel" className={linkClass('/hypotheses-funnel')}>
            <GitBranch className="h-5 w-5 flex-shrink-0" />
            Гипотезы / funnel
          </Link>
          <Link to="/promo-codes" className={linkClass('/promo-codes')}>
            <Tag className="h-5 w-5 flex-shrink-0" />
            Промокоды
          </Link>
          <Link to="/logs" className={linkClass('/logs')}>
            <ScrollText className="h-5 w-5 flex-shrink-0" />
            Логи
          </Link>

          <div>
            <button
              type="button"
              onClick={() => { setVacanciesOpen(!vacanciesOpen); setSeoOpen(false); }}
              className={`w-full ${linkClass('/vacancies')}`}
            >
              <Briefcase className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left">Вакансии</span>
              <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${vacanciesOpen ? 'rotate-180' : ''}`} />
            </button>
            {vacanciesOpen && (
              <div className="ml-4 mt-1 space-y-0.5">
                <Link
                  to="/vacancies/scraped"
                  onClick={() => setVacanciesOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm ${location.pathname.includes('scraped') ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  Парсерные
                </Link>
                <Link
                  to="/vacancies/internal"
                  onClick={() => setVacanciesOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm ${location.pathname.includes('internal') && !location.pathname.match(/\/internal\/\d+$/) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  Внутренние
                </Link>
              </div>
            )}
          </div>

          <Link to="/settings" className={linkClass('/settings')}>
            <Settings className="h-5 w-5 flex-shrink-0" />
            Настройки
          </Link>
          <Link to="/ai-analytics" className={linkClass('/ai-analytics')}>
            <Brain className="h-5 w-5 flex-shrink-0" />
            AI аналитика
          </Link>
          <Link to="/replicate-cost" className={linkClass('/replicate-cost')}>
            <Activity className="h-5 w-5 flex-shrink-0" />
            Replicate расход
          </Link>

          <div>
            <button
              type="button"
              onClick={() => { setSeoOpen(!seoOpen); setVacanciesOpen(false); }}
              className={`w-full ${linkClass('/seo')}`}
            >
              <Globe className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left">SEO</span>
              <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${seoOpen ? 'rotate-180' : ''}`} />
            </button>
            {seoOpen && (
              <div className="ml-4 mt-1 space-y-0.5">
                <Link
                  to="/seo/robots-sitemap"
                  onClick={() => setSeoOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm ${location.pathname === '/seo/robots-sitemap' || location.pathname === '/seo-settings' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  Robots & Sitemap
                </Link>
                <Link
                  to="/seo/pages"
                  onClick={() => setSeoOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm ${location.pathname === '/seo/pages' || location.pathname === '/page-seo' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  Страницы
                </Link>
              </div>
            )}
          </div>

          <Link to="/blog" className={linkClass('/blog')}>
            <BookOpen className="h-5 w-5 flex-shrink-0" />
            Блог
          </Link>
          <Link to="/stories" className={linkClass('/stories')}>
            <Sparkles className="h-5 w-5 flex-shrink-0" />
            Stories
          </Link>
        </nav>

        <div className="p-2 border-t border-gray-200">
          <div className="relative">
            <button
              type="button"
              onClick={() => setUserOpen(!userOpen)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <User className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left truncate">{adminUser.firstName || 'Admin'}</span>
              <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${userOpen ? 'rotate-180' : ''}`} />
            </button>
            {userOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900 truncate">{adminUser.fullName || adminUser.email}</p>
                  <p className="text-xs text-gray-500">{adminUser.role || 'admin'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { handleLogout(); setUserOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Выйти
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0" style={{ marginLeft: SIDEBAR_WIDTH }}>
        <div className="max-w-7xl w-full mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {userOpen && (
        <div className="fixed inset-0 z-20" onClick={() => setUserOpen(false)} aria-hidden="true" />
      )}
    </div>
  );
};

export default Layout;
